/**
 * CollisionAlert
 * ──────────────────────────────────────────────────────────────────────
 * Front-Desk "Needs your attention" collision card rendered when both
 * the original and the overbooked patient are physically present
 * (spec §Step 5 — Collision protocol).
 *
 * Pure UI; caller supplies collision state + handlers.
 */
import { Card, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { AlertTriangle, Sparkles } from 'lucide-react';

const CollisionAlert = ({
  collision,
  onConfirmAi,
  onHandleManually,
}) => {
  if (!collision?.active) return null;
  return (
    <Card className="border-amber-300 shadow-sm" data-testid="collision-alert">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-semibold text-slate-900">
            Collision — both patients present
          </span>
        </div>
        <div className="text-[11px] text-slate-700">
          <b>{collision.providerName}</b> · {collision.slotTime} slot
        </div>
        <div className="space-y-1">
          <div
            className="flex items-center gap-2 text-[11px] text-slate-700"
            data-testid="collision-original"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-slate-800 shrink-0" />
            <span className="font-medium">{collision.originalPatient.name}</span>
            <span className="text-slate-400">(original)</span>
            <span className="text-slate-500">— {collision.originalPatient.detail}</span>
          </div>
          <div
            className="flex items-center gap-2 text-[11px] text-slate-700"
            data-testid="collision-waitlist"
          >
            <span
              className="h-1.5 w-1.5 rounded-full shrink-0"
              style={{ border: '1px solid #64748b' }}
            />
            <span className="font-medium">{collision.waitlistPatient.name}</span>
            <span className="text-slate-400">(waitlist)</span>
            <span className="text-slate-500">— {collision.waitlistPatient.detail}</span>
          </div>
        </div>
        <div className="rounded-md bg-violet-50 border border-violet-200 p-2 text-[11px] space-y-1">
          <div className="flex items-center gap-1.5 text-violet-800">
            <Sparkles className="h-3 w-3" />
            <span className="font-medium">Echo AI</span>
          </div>
          <div className="text-slate-700">
            Next available slot today: <b>{collision.aiNextSlot}</b>.
          </div>
          <div className="text-slate-700">
            Recommending: {collision.aiRecommendation}.
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            className="h-8 text-xs bg-[#111] hover:bg-[#222]"
            onClick={onConfirmAi}
            data-testid="collision-confirm-ai"
          >
            Confirm Echo&apos;s recommendation
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={onHandleManually}
            data-testid="collision-manual"
          >
            Handle manually
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CollisionAlert;
